import { z } from 'zod'

export const ChatAction = {
  ShowMoreAudit: 'show-more-audit',
  EmailSupport: 'email-support',
  ViewArticle: 'view-article',
  ConfirmAction: 'confirm-action',
  CancelAction: 'cancel-action',
  SubmitInput: 'submit-input',
  SubmitInputGroup: 'submit-input-group',
  ChatAboutAction: 'chat-about-action',
} as const

export type ChatActionValue = (typeof ChatAction)[keyof typeof ChatAction]

export const ChatStreamStatus = {
  Pending: 'pending',
  Streaming: 'streaming',
  Completed: 'completed',
  Error: 'error',
} as const

export type ChatStreamStatusValue = (typeof ChatStreamStatus)[keyof typeof ChatStreamStatus]

export const chatStreamDocumentSchema = z.object({
  messageId: z.string(),
  supportChatId: z.string(),
  text: z.string().default(''),
  html: z.string().default(''),
  status: z.enum(['pending', 'streaming', 'completed', 'error']).default('pending'),
  thinkingStep: z.string().nullish(),
  thoughtText: z.string().nullish(),
  error: z.string().optional(),
  createdAt: z.unknown().nullable(),
  updatedAt: z.unknown().nullable(),
  completedAt: z.unknown().nullish(),
  metadata: z
    .object({
      ragGenerated: z.boolean().optional(),
      confidence: z.number().optional(),
      isAuditQuery: z.boolean().optional(),
    })
    .optional(),
})

export const chatStreamCreateSchema = z.object({
  messageId: z.string(),
  supportChatId: z.string(),
})

export const chatStreamUpdateSchema = z.object({
  text: z.string().optional(),
  html: z.string().optional(),
  status: z.enum(['pending', 'streaming', 'completed', 'error']).optional(),
  thinkingStep: z.string().nullish(),
  thoughtText: z.string().nullish(),
  error: z.string().optional(),
  metadata: z
    .object({
      ragGenerated: z.boolean().optional(),
      confidence: z.number().optional(),
      isAuditQuery: z.boolean().optional(),
      auditConfidence: z.number().optional(),
      hasAuditResults: z.boolean().optional(),
      hasKnowledgeResults: z.boolean().optional(),
      isAction: z.boolean().optional(),
      actionType: z.string().optional(),
      actionData: z
        .object({
          topic: z.string().optional(),
          articleId: z.string().optional(),
          originalTitle: z.string().optional(),
          confidence: z.number().optional(),
        })
        .optional(),
      actionLinks: z
        .array(
          z.object({
            url: z.string(),
            label: z.string(),
            description: z.string(),
            scope: z.enum(['brand', 'location', 'both']).optional(),
          })
        )
        .optional(),
      isFallback: z.boolean().optional(),
      pendingInputs: z
        .array(
          z.object({
            inputType: z.enum(['select', 'date']),
            prompt: z.string(),
            options: z.array(z.object({ value: z.string(), label: z.string() })).optional(),
            contextKey: z.string(),
            placeholder: z.string().optional(),
          })
        )
        .optional(),
      pendingAction: z
        .object({
          actionId: z.string(),
          params: z.record(z.string(), z.unknown()),
          riskLevel: z.enum(['low', 'medium', 'high']),
          displayTitle: z.string(),
          confirmPrompt: z.string(),
          details: z.array(z.object({ label: z.string(), value: z.string() })),
        })
        .optional(),
      actionResult: z
        .object({
          success: z.boolean(),
          actionId: z.string(),
          message: z.string(),
        })
        .optional(),
      componentSpec: z.record(z.string(), z.unknown()).optional(),
    })
    .optional(),
})

export type ChatStreamDocument = z.infer<typeof chatStreamDocumentSchema>
export type ChatStreamCreate = z.infer<typeof chatStreamCreateSchema>
export type ChatStreamUpdate = z.infer<typeof chatStreamUpdateSchema>
